import React from "react";
import "./SearchableSelect.css";

export interface SearchableSelectOption {
  value: string;
  label: string;
}

interface SearchableSelectProps {
  options: SearchableSelectOption[];
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  searchPlaceholder?: string;
  noResultsText?: string;
  invalid?: boolean;
  dir?: "ltr" | "rtl";
}

const SearchableSelect: React.FC<SearchableSelectProps> = ({
  options,
  value,
  onChange,
  placeholder = "Select",
  searchPlaceholder = "Search",
  noResultsText = "No results",
  invalid = false,
  dir = "ltr",
}) => {
  const [open, setOpen] = React.useState(false);
  const [query, setQuery] = React.useState("");
  const rootRef = React.useRef<HTMLDivElement>(null);
  const searchRef = React.useRef<HTMLInputElement>(null);

  const selected = options.find((o) => o.value === value);

  const filtered = React.useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return options;
    return options.filter((o) => o.label.toLowerCase().includes(q));
  }, [options, query]);

  // close on outside click
  React.useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [open]);

  // focus the search box when the panel opens, reset the query when it closes
  React.useEffect(() => {
    if (open) {
      const id = window.setTimeout(() => searchRef.current?.focus(), 0);
      return () => window.clearTimeout(id);
    }
    setQuery("");
  }, [open]);

  const choose = (val: string) => {
    onChange(val);
    setOpen(false);
  };

  return (
    <div
      className={`ds-select${open ? " is-open" : ""}${
        invalid ? " is-invalid" : ""
      }`}
      ref={rootRef}
      dir={dir}
    >
      <button
        type="button"
        className="ds-select__control"
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <span
          className={`ds-select__value${
            selected ? "" : " ds-select__value--placeholder"
          }`}
        >
          {selected ? selected.label : placeholder}
        </span>
        <i className="fas fa-chevron-down ds-select__chevron" />
      </button>

      {open && (
        <div className="ds-select__panel">
          <div className="ds-select__search">
            <i className="fas fa-search ds-select__search-icon" />
            <input
              ref={searchRef}
              type="text"
              className="ds-select__search-input"
              placeholder={searchPlaceholder}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
          </div>

          <div className="ds-select__options" role="listbox">
            {filtered.length === 0 ? (
              <div className="ds-select__empty">{noResultsText}</div>
            ) : (
              filtered.map((option) => (
                <div
                  key={option.value}
                  role="option"
                  aria-selected={option.value === value}
                  className={`ds-select__option${
                    option.value === value ? " is-active" : ""
                  }`}
                  onClick={() => choose(option.value)}
                >
                  <span>{option.label}</span>
                  {option.value === value && (
                    <i className="fas fa-check ds-select__option-check" />
                  )}
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default SearchableSelect;
